import axios from 'axios';
import { sanitizeForResume } from './textSanitizer';

const shortBackoff = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MobileGeminiService = {
    generateText: async (field, currentText, baseCharacterLimit, updateStatus) => {
        console.log(`Generating text for ${field} with current text:`, currentText);
    // API keys managed server-side via /api/ai proxy

        const adjustedCharacterLimit = (contentLength, baseLimit) => {
            return contentLength > 2000 ? baseLimit - 50 : baseLimit;
        };

        const textStr = currentText ? currentText.toString() : '';
        const characterLimit = adjustedCharacterLimit(textStr.length, baseCharacterLimit);

        const PLAIN_TEXT_RULE = 'Output PLAIN TEXT only. Do NOT use markdown: no asterisks (*), no double asterisks (**), no hash symbols (#), no dashes (-) or bullets (•) at the start of lines. Separate items with newlines only.';

        let prompt;
        switch (field) {
            case 'title':
                prompt = `Generate a concise, professional job title for a resume. The title must be between 3 to 5 words and not exceed ${characterLimit} characters. Example: "Software Engineer", "Project Manager". ${PLAIN_TEXT_RULE}\n\nBased on this information, output only the job title:\n${currentText}`;
                break;
            case 'summary':
                prompt = `You are an expert resume writer. Rewrite and enhance the following summary for a resume in 2-3 sentences. Highlight key skills, experience, and professional objectives concisely. ${PLAIN_TEXT_RULE} Do not include a section label like "Summary:".\n\nOriginal:\n${currentText}`;
                break;
            case 'experience':
                prompt = `Rewrite and improve the following professional experience for a resume. Use short, achievement-focused statements. Quantify results where possible. Keep each statement on its own line. Maximum 6-8 statements. ${PLAIN_TEXT_RULE} Do not include a section label like "Experience:".\n\nOriginal:\n${currentText}`;
                break;
            case 'skills':
                prompt = `Rewrite the following skills for a resume. Return a comma-separated list of 6-10 key skill names only. Do not add descriptions, categories, or elaboration. Example output: "Python, React, AWS, Project Management, Data Analysis"\n${PLAIN_TEXT_RULE}\n\nOriginal:\n${currentText}`;
                break;
            case 'education':
                prompt = `Rewrite the following education section for a resume. Include degree, institution, and year. Keep each entry on its own line. Maximum 3-4 entries. ${PLAIN_TEXT_RULE} Do not include a section label like "Education:".\n\nOriginal:\n${currentText}`;
                break;
            case 'publications':
                prompt = `Rewrite the following publications for a resume. List each publication with title, date, and relevance. Keep each entry on its own line. Maximum 4-5 entries. ${PLAIN_TEXT_RULE} Do not include a section label.\n\nOriginal:\n${currentText}`;
                break;
            case 'coverLetter': 
                prompt = `Write a professional cover letter for a ${currentText.jobTitle} position at ${currentText.companyName}. Highlight relevant experience, skills, and accomplishments. Keep it concise — no longer than 400 words. ${PLAIN_TEXT_RULE}\n\nResume Details:\nName: ${currentText.name}\nTitle: ${currentText.title}\nSummary: ${currentText.summary}\nExperience: ${currentText.experience}\nSkills: ${currentText.skills}\nEducation: ${currentText.education}`;
                break;
            default:
                prompt = `Rewrite and improve the following ${field} for a resume, maintaining its core content but enhancing clarity and impact. ${PLAIN_TEXT_RULE} Do not exceed ${characterLimit} characters. Do not include a section label.\n\nOriginal:\n${currentText}`;
        }

        updateStatus(`Generating ${field}...`);

        let generatedText = '';
        let retries = 0;
        const maxRetries = 3;

    do {
            try {
                    const response = await axios.post('/api/ai/gemini/chat', {
                    model: 'gemini-2.5-flash-lite',
                    messages: [
                        { role: "system", content: "You are a professional resume writer. Output plain text only. Never use markdown formatting, asterisks, hash symbols, or bullet characters." },
                        { role: "user", content: prompt }
                    ],
                    max_tokens: 250,
                    n: 1,
                    stop: null,
                    temperature: 0.5,
                    actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
                });

                generatedText = ((response && response.data && response.data.choices && response.data.choices[0] && response.data.choices[0].message && response.data.choices[0].message.content) ? response.data.choices[0].message.content : '').trim();
                // Sanitize AI output to strip any markdown artifacts
                generatedText = sanitizeForResume(generatedText, { maxLength: characterLimit });

                if (!generatedText) {
                    updateStatus(`Retrying ${field} generation...`);
                } else if (generatedText.length <= characterLimit) {
                    updateStatus(`${field} generation complete.`);
                    return generatedText;
                } else {
                    updateStatus(`Retrying ${field} generation...`);
                }
            } catch (error) {
                console.error("Error generating text: ", error.response ? error.response.data : error.message);
                if (error.response && error.response.status === 429) {
                    await new Promise(res => setTimeout(res, 1000));
                } else {
                    throw error;
                }
            }

            retries++;
    } while (((!generatedText) || generatedText.length > characterLimit) && retries < maxRetries);

        if (!generatedText) {
            return (currentText || '').toString().trim();
        }

        if (generatedText.length > characterLimit) {
            generatedText = sanitizeForResume(generatedText, { maxLength: characterLimit });
        }

        updateStatus(`${field} generation complete.`);
        return generatedText;
    },

    validateAndAdjust: async (field, generatedText, baseCharacterLimit, updateStatus) => {
    console.log(`Validating and adjusting text for ${field}.`);

        const adjustedCharacterLimit = (contentLength, baseLimit) => {
            return contentLength > 2000 ? baseLimit - 50 : baseLimit;
        };

        const characterLimit = adjustedCharacterLimit(generatedText.length, baseCharacterLimit);

        let prompt = `The following section was generated for a resume. Review, adjust, and refine it to be concise and impactful. Output PLAIN TEXT only — do NOT use asterisks (*), double asterisks (**), hash symbols (#), dashes (-) as bullets, or any markdown formatting. Remove any section labels like "Skills:" or "Summary:". The final output must not exceed ${characterLimit} characters.\n\n${generatedText}`;

        updateStatus(`Validating ${field}...`);

        let validatedText = '';
        let retries = 0;
        const maxRetries = 3;

    do {
            try {
                const response = await axios.post('/api/ai/gemini/chat', {
                    model: 'gemini-2.5-flash-lite',
                    messages: [
                        { role: "system", content: "You are a professional resume editor. Output plain text only. Never use markdown formatting, asterisks, hash symbols, or bullet characters." },
                        { role: "user", content: prompt }
                    ],
                    max_tokens: 250,
                    n: 1,
                    stop: null,
                    temperature: 0.4,
                    actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
                });

                validatedText = ((response && response.data && response.data.choices && response.data.choices[0] && response.data.choices[0].message && response.data.choices[0].message.content) ? response.data.choices[0].message.content : '').trim();
                // Sanitize validated output
                validatedText = sanitizeForResume(validatedText, { maxLength: characterLimit });

                if (!validatedText) {
                    updateStatus(`Retrying ${field} validation...`);
                } else if (validatedText.length <= characterLimit) {
                    updateStatus(`${field} validation complete.`);
                    return validatedText;
                } else {
                    updateStatus(`Retrying ${field} validation...`);
                }
            } catch (error) {
                console.error("Error validating text: ", error.response ? error.response.data : error.message);
                if (error.response && error.response.status === 429) {
                    await new Promise(res => setTimeout(res, 1000));
                } else {
                    throw error;
                }
            }

            retries++;
            await shortBackoff(300 * retries);
        } while (((!validatedText) || validatedText.length > characterLimit) && retries < maxRetries);

        if (!validatedText) {
            return (generatedText || '').toString().trim();
        }

        if (validatedText.length > characterLimit) {
            validatedText = sanitizeForResume(validatedText, { maxLength: characterLimit });
        }

        updateStatus(`${field} validation complete.`);
        return validatedText;
    },

    extractRelevantContent: async (field, messageContent) => {
        try {
            // API key handled by backend proxy
            // Per-field extraction rules for clean, resume-ready output
            const fieldRules = {
                skills: 'Extract only the skill names mentioned. Return as a comma-separated list of 6-10 skills maximum. Example: "Python, React, AWS, Docker". No descriptions or elaboration.',
                summary: 'Extract and return a professional summary of 2-3 sentences only. No section label.',
                experience: 'Extract work experience as short, achievement-focused statements. One statement per line. Maximum 6-8 statements. No section label.',
                education: 'Extract education details: degree, institution, and year. One entry per line. Maximum 3-4 entries. No section label.',
                publications: 'Extract publication entries: title, date, relevance. One entry per line. Maximum 4-5 entries. No section label.',
            };
            const rule = fieldRules[field] || `Extract the relevant content for the ${field} section. Be concise.`;

            const prompt = `Extract content from the message below that belongs in the "${field}" section of a resume.

RULES:
- Output PLAIN TEXT only. No markdown, no asterisks (*), no hash symbols (#), no bullet characters (-, •, ▸).
- ${rule}
- If no relevant content is found, return an empty response.

Message:
${messageContent}`;

                const response = await axios.post('/api/ai/gemini/chat', {
                model: 'gemini-2.5-flash-lite',
                messages: [
                    { role: "system", content: "You are a resume content extractor. Output plain text only. Never use markdown formatting, asterisks, hash symbols, or bullet characters." },
                    { role: "user", content: prompt }
                ],
                max_tokens: 250,
                n: 1,
                stop: null,
                temperature: 0.3,
                actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
            });

            let extractedContent = response.data.choices[0].message.content.trim();
            // Sanitize extracted content as safety net
            extractedContent = sanitizeForResume(extractedContent);

            if (extractedContent.length > 0) {
                return extractedContent;
            } else {
                return '';
            }
        } catch (error) {
            console.error(`Error extracting content for ${field}:`, error);
            throw error;
        }
    },

    detectSectionLabels: async (messageContent) => {
        try {
            // API key handled by backend proxy
            const prompt = `
                Analyze the following message and identify any section labels such as "Summary," "Experience," "Skills," "Education," or "Publications." 
                Return a list of detected labels along with their positions in the text.\n\nMessage:\n${messageContent}`;

                const response = await axios.post('/api/ai/gemini/chat', {
                model: 'gemini-2.5-flash-lite',
                messages: [{ role: "user", content: prompt }],
                max_tokens: 250,
                n: 1,
                stop: null,
                temperature: 0.7,
                actionType: 'resume_edit'
            });

            return response.data.choices[0].message.content.trim();
        } catch (error) {
            console.error('Error detecting section labels:', error);
            throw error;
        }
    },
};

export default MobileGeminiService;
